import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import type { FunctionComponent } from 'react';

import { EXPIRED_ERROR } from '../../../shared/publicPortal/errors';
import { errorMessagesListFromResponse, type FailedFetchResponse } from '../../helpers/errors';
import useAsyncValue from '../../hooks/useAsyncValue';
import { post, remove } from '../../redux/api';
import { getCurrentUser } from '../api/users';
import { tokenAuthenticationRoute } from '../helpers/routes';
import useSlug from '../hooks/useSlug';
import useCurrentPortalId from '../OrganizationPortal/hooks/useCurrentPortalId';
import type { CurrentUser } from '../../../shared/types/routes/publicPortal/users';

export type UserContext = CurrentUser & {
  isLoggedIn: boolean;
  isTokenAuthenticated: boolean;
  setToken: (token: string) => Promise<void>;
  removeToken: () => Promise<void>;
  isLoading: boolean;
  tokenError: string | null;
};

export const CurrentUserContext = createContext<UserContext>({
  isLoggedIn: false,
  isTokenAuthenticated: false,
  setToken: async () => {},
  removeToken: async () => {},
  isLoading: false,
  tokenError: null,
});

const CurrentUserProvider: FunctionComponent = ({ children }) => {
  const slug = useSlug();
  const [tokenError, setTokenError] = useState<string | null>(null);
  const { value: portalIdValue, isLoading: isLoadingPortalId, errors: portalIdError } = useCurrentPortalId();
  const portalId = portalIdValue?.id;
  useEffect(() => {
    // Token authentication won't work if portalId is not available.
    // Display an error message to provide feedback to the user.
    if (portalIdError) {
      setTokenError('Something went wrong! Token authentication is unavailable.');
    }
  }, [portalIdError]);
  const { setPending, value, isLoading, errors, setResolve, setReject } = useAsyncValue<CurrentUser>();
  const fetchUser = useCallback(async () => {
    setPending();
    try {
      const response = await getCurrentUser();
      if (response.data) {
        setResolve(response.data);
      }
    } catch (error) {
      setReject(errorMessagesListFromResponse(error as FailedFetchResponse).map((e) => e.toString()));
    }
  }, [setPending, setReject, setResolve]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const setToken = useCallback(
    async (token: string) => {
      try {
        setTokenError(null);
        await post<{ token: string }, void>(tokenAuthenticationRoute(slug), { token });
      } catch (error) {
        const errorMessages = errorMessagesListFromResponse(error as FailedFetchResponse).map((e) => e.message);
        if (errorMessages.includes(EXPIRED_ERROR)) {
          setTokenError('This token has expired. Please contact the vendor for a new token.');
        } else {
          setTokenError('Token incorrect! Try again.');
        }
      }
      await fetchUser();
    },
    [fetchUser, slug],
  );

  const removeToken = useCallback(
    async () => {
      try {
        setTokenError(null);
        await remove(tokenAuthenticationRoute(slug));
      } catch {
        setTokenError('Sign out token failed! Try again.');
      }
      await fetchUser();
    },
    [fetchUser, slug],
  );

  const context: UserContext = useMemo(() => {
    const isLoadingCurrentUser = isLoadingPortalId || isLoading;
    if (isLoadingCurrentUser || errors?.length || !value) {
      return {
        isLoggedIn: false,
        isTokenAuthenticated: false,
        setToken,
        removeToken,
        isLoading: isLoadingCurrentUser,
        tokenError,
      };
    }

    const isLoggedIn = !!value.user || !!value.portals?.length;
    const isTokenAuthenticated = !!(portalId && value.portals?.includes(portalId?.toString()));
    return { isLoggedIn, isTokenAuthenticated, setToken, removeToken, isLoading, tokenError, ...value };
  }, [isLoadingPortalId, isLoading, errors?.length, value, portalId, setToken, removeToken, tokenError]);

  return <CurrentUserContext.Provider value={context}>{children}</CurrentUserContext.Provider>;
};

export default CurrentUserProvider;
